import React, { useEffect, useRef } from 'react'
import FakeMediaStreamTrack from '../core/AudioEngine/FakeMediaStreamTrack'
import { AudioTrackWithInfo } from '../UserContext'
import { env } from '../services/EnvironmentVariables'

const volumeMeterAudioContext = new AudioContext()
/* @ts-ignore */
window.volumeMeterAudioContext = volumeMeterAudioContext

const fftSize = 32

type InStream = AudioTrackWithInfo | MediaStreamTrack | FakeMediaStreamTrack

interface VolumeMeterProps {
    stream: InStream | InStream[]
    channelCount?: number
    chgrp?: number[]
    fps?: number
    width?: number
    height?: number
    style?: React.CSSProperties
    isMuted?: boolean
    mutedStateTooltipText?: string
    grayScale?: boolean
    variant?: 'pills' | 'bars'
}

export function VolumeMeter(props: VolumeMeterProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const width = useRef<number>(6)

    const channelCount = props.channelCount ?? 2
    const fps = props.fps ?? 16
    const variant = props.variant ?? 'bars'
    const height = props.height ?? (variant === 'pills' ? 29 : 20)

    useEffect(() => {
        const canvasNode = canvasRef.current

        function channelCtToCanvasWidth(c: number) {
            if (variant === 'bars') {
                return c > 1 ? 12 : 6
            } else if (variant === 'pills') {
                return c > 1 ? 14 : 6
            } else {
                throw new Error('Unreachable')
            }
        }

        if (!props.stream || (Array.isArray(props.stream) && props.stream.length === 0)) {
            return
        }

        if (env.TEST_NO_VOLUME_METERS) {
            return
        }

        let levelSamplers: LevelSampler[] = []
        let maxChannelCount = 0
        if (Array.isArray(props.stream)) {
            for (const stream of props.stream) {
                const sampler = createLevelSampler(stream, props.chgrp)
                if (sampler) {
                    levelSamplers.push(sampler)
                }
                let chCount = getChannelCount(stream, channelCount, props.chgrp)
                if (chCount > maxChannelCount) {
                    maxChannelCount = chCount
                }
            }
        } else {
            const sampler = createLevelSampler(props.stream, props.chgrp)
            if (sampler) {
                levelSamplers.push(sampler)
            }
            let chCount = getChannelCount(props.stream, channelCount, props.chgrp)
            if (chCount > maxChannelCount) {
                maxChannelCount = chCount
            }
        }

        if (canvasNode) {
            width.current = channelCtToCanvasWidth(maxChannelCount)
            canvasNode.width = width.current
        }

        const render = createRenderFunc({
            canvas: canvasNode,
            levelSamplers,
            channelCount: maxChannelCount,
            grayScale: props.grayScale ?? false,
            variant,
            isMuted: props.isMuted ?? false,
        })

        if (render) {
            batchVolumeMeterRenderer.addDrawFunc(render)
        }

        return () => {
            if (render) {
                batchVolumeMeterRenderer.removeDrawFunc(render)
            }

            for (const sampler of levelSamplers) {
                sampler.cleanup?.()
            }
        }
    }, [props.stream, fps, channelCount, props.isMuted, props.grayScale, variant, props.chgrp])

    if (env.TEST_NO_VOLUME_METERS) {
        return null
    }

    return (
        <div style={{ width: `${width}px`, height: `${height}px` }}>
            <canvas ref={canvasRef} width={0} height={height} style={{ border: 'none', ...props.style }} />
        </div>
    )
}

type LevelSampler = {
    sample: () => number[]
    cleanup?: () => void
}
function createLevelSampler(stream: InStream, chgrp?: number[]): LevelSampler | null {
    if (isProAudio(stream)) {
        return {
            sample: () => stream.getCurrentOutputVolume(chgrp ?? [0, 1]).map((v) => (v || 0) / 100),
        }
    }

    // TODO: Allow splitter and analyzers to be shared when multiple mediastreamtracks are used
    if (isMediaStreamTrack(stream) || isAudioTrackWithInfo(stream)) {
        const splitter = volumeMeterAudioContext.createChannelSplitter(2)
        const mediaStreamAudioSourceNode = volumeMeterAudioContext.createMediaStreamSource(new MediaStream([stream]))

        const analyserNodeCh0 = volumeMeterAudioContext.createAnalyser()
        const analyserNodeCh1 = volumeMeterAudioContext.createAnalyser()

        analyserNodeCh0.fftSize = fftSize
        analyserNodeCh1.fftSize = fftSize

        mediaStreamAudioSourceNode.connect(splitter)
        splitter.connect(analyserNodeCh0, 0, 0)
        splitter.connect(analyserNodeCh1, 1, 0)
        volumeMeterAudioContext.resume()

        const pcmDataCh0 = new Float32Array(analyserNodeCh0.fftSize)
        const pcmDataCh1 = new Float32Array(analyserNodeCh1.fftSize)

        return {
            sample: () => {
                analyserNodeCh0.getFloatTimeDomainData(pcmDataCh0)
                analyserNodeCh1.getFloatTimeDomainData(pcmDataCh1)

                let sumSquaresCh0 = 0.0
                for (const amplitude of pcmDataCh0) {
                    sumSquaresCh0 += amplitude * amplitude
                }

                let sumSquaresCh1 = 0.0
                for (const amplitude of pcmDataCh1) {
                    sumSquaresCh1 += amplitude * amplitude
                }

                return [Math.sqrt(sumSquaresCh0 / pcmDataCh0.length), Math.sqrt(sumSquaresCh1 / pcmDataCh1.length)]
            },
            cleanup: () => {
                if (mediaStreamAudioSourceNode) {
                    try {
                        mediaStreamAudioSourceNode.disconnect()
                    } catch (e) {
                        console.error(e)
                    }
                }

                if (splitter) {
                    try {
                        splitter.disconnect()
                    } catch (e) {
                        console.error(e)
                    }
                }

                if (analyserNodeCh0) {
                    try {
                        analyserNodeCh0.disconnect()
                    } catch (e) {
                        console.error(e)
                    }
                }

                if (analyserNodeCh1) {
                    try {
                        analyserNodeCh1.disconnect()
                    } catch (e) {
                        console.error(e)
                    }
                }
            },
        }
    }

    return null
}

function getChannelCount(stream: InStream, forcedChannelCount?: number, chgrp?: number[]) {
    if (isProAudio(stream)) {
        if (chgrp) {
            return chgrp.length
        }
        return stream.channelSelection.map((ch) => ch.length).reduce((n, len) => (len > n ? len : n), 0)
    }

    if (isAudioTrackWithInfo(stream)) {
        return stream.info.channelSelection.flat().length
    }

    return forcedChannelCount ?? 2
}

type RenderOptions = {
    canvas: HTMLCanvasElement | null
    levelSamplers: LevelSampler[]
    channelCount: number
    grayScale: boolean
    variant?: 'pills' | 'bars'
    isMuted: boolean
}

function createRenderFunc(opts: RenderOptions): (() => void) | null {
    if (opts.levelSamplers.length === 0) {
        return null
    }

    return () => {
        if (!opts.canvas) {
            return
        }

        let leftVolume = 0.0
        let rightVolume = 0.0
        for (const levelSampler of opts.levelSamplers) {
            const [left, right] = levelSampler.sample()
            leftVolume += left
            rightVolume += right
        }
        leftVolume = scaleVolume(leftVolume)
        rightVolume = scaleVolume(rightVolume)

        drawMeterOnCanvas({
            canvas: opts.canvas,
            channelCount: opts.channelCount,
            leftVolume,
            rightVolume,
            grayScale: opts.grayScale,
            variant: opts.variant,
            isMuted: opts.isMuted,
        })
    }
}

function isProAudio(stream: InStream): stream is FakeMediaStreamTrack {
    return stream instanceof FakeMediaStreamTrack
}
function isMediaStreamTrack(stream: InStream): stream is MediaStreamTrack {
    return stream instanceof MediaStreamTrack && stream.kind === 'audio'
}
function isAudioTrackWithInfo(stream: InStream): stream is AudioTrackWithInfo {
    return 'info' in stream
}

function scaleVolume(value: number) {
    const scaleFactor = 10000
    value = Math.log10(value * scaleFactor) / Math.log10(scaleFactor)

    const volumeThreshold = 0.1
    value = value < volumeThreshold ? 0.1 : Math.max(value * 1.2 - 0.2, 0.1)

    return value
}

const drawMeterOnCanvas = ({
    canvas,
    channelCount,
    leftVolume,
    rightVolume,
    grayScale,
    variant,
    isMuted,
}: {
    canvas: HTMLCanvasElement
    channelCount: number
    leftVolume: number
    rightVolume: number
    grayScale?: boolean
    variant?: 'pills' | 'bars'
    isMuted: boolean
}) => {
    const ctx = canvas.getContext('2d')
    if (!ctx) {
        return
    }

    const channelWidth = canvas.width / (channelCount || 1)
    const channelHeight = canvas.height
    const margin = channelCount === 1 ? 0 : 1

    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    let drawFunc = (variant ?? 'bars') === 'bars' ? drawChannelVolumeMeter : drawChannelVolumeMeterPills

    // Draw left channel
    drawFunc({
        ctx,
        x: 0,
        channelHeight,
        channelWidth: channelWidth - margin,
        volume: leftVolume,
        grayScale: grayScale,
        isMuted: isMuted,
    })

    if (channelCount > 1) {
        // Draw right channel
        drawFunc({
            ctx,
            x: channelWidth + margin,
            channelHeight,
            channelWidth: channelWidth - margin,
            volume: rightVolume,
            grayScale: grayScale,
            isMuted: isMuted,
        })
    }
}

const drawChannelVolumeMeter = ({
    ctx,
    x,
    channelHeight,
    channelWidth,
    volume,
    grayScale,
    isMuted,
}: {
    ctx: CanvasRenderingContext2D
    x: number
    channelHeight: number
    channelWidth: number
    volume: number
    grayScale?: boolean
    isMuted: boolean
}) => {
    // Green section (0-80%)
    let greenSectionHeight = channelHeight * 0.8
    ctx.fillStyle = grayScale ? '#CECECE' : 'rgb(69, 196, 69)' // green
    ctx.fillRect(x, channelHeight - greenSectionHeight * volume, channelWidth, greenSectionHeight * volume)

    // Yellow section (80-90%)
    if (volume > 0.75) {
        let yellowSectionHeight = channelHeight * 0.1
        ctx.fillStyle = grayScale ? '#CECECE' : 'rgb(255, 255, 0)' // yellow
        let yellowSectionFilled = (yellowSectionHeight * (volume - 0.8)) / 0.1
        ctx.fillRect(
            x,
            channelHeight - greenSectionHeight * 0.8 - yellowSectionFilled,
            channelWidth,
            yellowSectionFilled
        )
    }

    // Red section (90-100%)
    if (volume > 0.9) {
        let redSectionHeight = channelHeight * 0.1
        ctx.fillStyle = grayScale ? '#CECECE' : 'rgb(255, 0, 0)' // red
        let redSectionFilled = (redSectionHeight * (volume - 0.9)) / 0.1
        ctx.fillRect(
            x,
            channelHeight - greenSectionHeight * 0.8 - channelHeight * 0.1 - redSectionFilled,
            channelWidth,
            redSectionFilled
        )
    }
}

const drawChannelVolumeMeterPills = ({
    ctx,
    x,
    channelHeight,
    channelWidth,
    volume,
    grayScale,
    isMuted,
}: {
    ctx: CanvasRenderingContext2D
    x: number
    channelHeight: number
    channelWidth: number
    volume: number
    grayScale?: boolean
    isMuted: boolean
}) => {
    const pillHeight = 1.5
    const numPills = 9

    const activeColor = isMuted ? '#666' : '#85E0A3'
    const warnColor = 'orange'
    const dangerColor = '#FF0000'
    const inactiveColor = '#444'

    for (let i = 0; i < numPills; i++) {
        const isActive = volume * 9 >= i
        let color = inactiveColor
        if (isActive) {
            if (i >= 8) {
                color = dangerColor
            } else if (i >= 6) {
                color = warnColor
            } else {
                color = activeColor
            }
        }
        const offset = pillHeight * 2 * i
        const y = channelHeight - pillHeight - 1 - offset
        drawPill(ctx, x + 1, y, color)
    }
}

/**
 * Draws a single pill at (x, y) on the given canvas context.
 * @param {CanvasRenderingContext2D} ctx - The canvas 2D context.
 * @param {number} x - The x coordinate (top-left of pill).
 * @param {number} y - The y coordinate (top-left of pill).
 */
function drawPill(ctx: CanvasRenderingContext2D, x: number, y: number, color: string) {
    const pillWidth = 4
    const pillHeight = 1.5
    const pillRadius = pillHeight / 2

    ctx.beginPath()
    ctx.moveTo(x + pillRadius, y)
    ctx.lineTo(x + pillWidth - pillRadius, y)
    ctx.arcTo(x + pillWidth, y, x + pillWidth, y + pillRadius, pillRadius)
    ctx.lineTo(x + pillWidth, y + pillHeight - pillRadius)
    ctx.arcTo(x + pillWidth, y + pillHeight, x + pillWidth - pillRadius, y + pillHeight, pillRadius)
    ctx.lineTo(x + pillRadius, y + pillHeight)
    ctx.arcTo(x, y + pillHeight, x, y + pillHeight - pillRadius, pillRadius)
    ctx.lineTo(x, y + pillRadius)
    ctx.arcTo(x, y, x + pillRadius, y, pillRadius)
    ctx.closePath()
    ctx.fillStyle = color
    ctx.fill()
}

class BatchAnimationFrameRenderer {
    private drawFuncs: Set<() => void> = new Set()
    private lastTime: number = 0
    private readonly interval: number

    constructor(targetFps: number = 16) {
        this.interval = 1000 / targetFps
        this.render = this.render.bind(this)
    }

    addDrawFunc(drawFunc: () => void) {
        this.drawFuncs.add(drawFunc)
        if (this.drawFuncs.size === 1) {
            requestAnimationFrame(this.render)
        }
    }

    removeDrawFunc(drawFunc: () => void) {
        this.drawFuncs.delete(drawFunc)
    }

    private render(timestamp: number) {
        const current = timestamp
        const elapsed = current - this.lastTime

        if (elapsed >= this.interval) {
            this.lastTime = current
            this.drawFuncs.forEach((drawFunc) => drawFunc())
        }

        if (this.drawFuncs.size > 0) {
            requestAnimationFrame(this.render)
        }
    }
}

const batchVolumeMeterRenderer = new BatchAnimationFrameRenderer()
